import CheckboxTree from 'react-checkbox-tree';
import { appContext } from "../../../../AppContext";
import { Node as CheckBoxTreeNode } from "react-checkbox-tree";
import { IbssComponent } from "../../../../Components/Core/BaseComponent/IbssComponent";
import { Helper } from './Helper';
import AppPermissionsDialog from './AppPermissionsDialog';
import { JSX } from 'react';
import { SerendieSymbol } from '@serendie/symbols';
import { Button, ButtonBar, Card, GridContainer, GridItem, RightButtons, Typography } from '@iconics/components';
import { NodeData } from "../../../../Providers.LocalStorage/Models";
import { LanguageValuesHelper } from '@iconics/localisation';

export default class AppPermissions extends IbssComponent<IProps, IState>
{
    private helper = new Helper();
    private languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            clickedPermissionId: '',
            showDialog: false
        };
    }

    private get permissionNodes(): CheckBoxTreeNode[]
    {
        const nodes = this.props.permissions
            .filter(i => !!i.area && !i.category && !i.permission)
            .map(area => ({
                value: this.helper.fullyQualifiedName(area),
                label: area.area,
                children: this.props.permissions
                    .filter(i => i.area == area.area && !!i.category && !i.permission)
                    .map(category => ({
                        value: this.helper.fullyQualifiedName(category),
                        label: category.category,
                        children: this.props.permissions
                            .filter(i => i.area == area.area && i.category == category.category && !!i.permission)
                            .map(permission => ({
                                value: this.helper.fullyQualifiedName(permission),
                                label: permission.permission,
                            })),
                    })),
            }));

        return nodes;
    }

    private get checkedPermissions(): string[]
    {
        return this.props.permissions.filter(i => i.isSelected).map(i => this.helper.fullyQualifiedName(i));
    }

    private get expandedPermissions(): string[]
    {
        return this.props.permissions.filter(i => i.isExpanded).map(i => this.helper.fullyQualifiedName(i));
    }

    private handlePermissionCheck(checked: string[]): void
    {
        const permissions = this.props.permissions
            .map(i => ({
                ...i,
                isSelected: checked.contains(this.helper.fullyQualifiedName(i)),
            }));
        this.props.onPermissionsChange(permissions);
    }

    private handlePermissionExpand(expanded: string[]): void
    {
        const permissions = this.props.permissions
            .map(i => ({
                ...i,
                isExpanded: expanded.contains(this.helper.fullyQualifiedName(i)),
            }));
        this.props.onPermissionsChange(permissions);
    }

    private get nodeTree(): CheckBoxTreeNode[]
    {
        const nodeTree = appContext().localStorageProvider.getNodeData().tree<NodeData, CheckBoxTreeNode>(
            i => i.ID.toString(),
            i => i.Parent_ID?.toString() ?? null,
            (node, children) => ({
                value: node.ID.toString(),
                label: this.languageValuesHelper.getValue(this.languageValuesHelper.fromString(node.DisplayName)) || node.Name,
                children: children,
            } as CheckBoxTreeNode)
        );
        return nodeTree;
    }

    private get checkedNodeIds(): string[]
    {
        const clickedPermission = this.props.permissions.find(i => this.helper.fullyQualifiedName(i) == this.state.clickedPermissionId);
        if (!clickedPermission)
        {
            return [];
        }
        return clickedPermission.nodeIds;
    }

    private handleNodeCheck(checkedNodeIds: string[]): void
    {
        const permissions = this.props.permissions
            .map(i => ({
                ...i,
                nodeIds: (this.helper.fullyQualifiedName(i) == this.state.clickedPermissionId ? checkedNodeIds : i.nodeIds),
            }));

        this.props.onPermissionsChange(permissions);
    }

    public render(): JSX.Element
    {
        const labels = appContext().labels;

        return (
            <>
                <Card
                    title={labels.Platform_Role_AppActionsTitle}
                    subTitle={labels.Platform_Role_AppActionsSubTitle}
                    icon={<SerendieSymbol name='apps-circle' />}
                >
                    <GridContainer>
                        <GridItem expanded={6} >
                            <Typography role='title'>
                                {labels.HubLabelUserPermissionCol1}
                            </Typography>
                            <Typography role='body'>
                                {labels.HubLabelUserPermissionDescriptionCol1}
                            </Typography>
                            <GridItem >
                                {
                                    <CheckboxTree
                                        icons={{
                                            check: <SerendieSymbol name='check-square' />,
                                            uncheck: <SerendieSymbol name='square' />,
                                            expandClose: <SerendieSymbol name='chevron-right' />,
                                            expandOpen: <SerendieSymbol name='chevron-down' />,
                                            halfCheck: <SerendieSymbol name='minus-square' />,
                                        }}
                                        nodes={this.permissionNodes}
                                        checked={this.checkedPermissions}
                                        expanded={this.expandedPermissions}
                                        showNodeIcon={false}
                                        onCheck={checked => this.handlePermissionCheck(checked)}
                                        onExpand={expandedPermissions => this.handlePermissionExpand(expandedPermissions)}
                                        onClick={i => this.setState({ clickedPermissionId: i.value })}
                                    />
                                }
                            </GridItem>
                        </GridItem>
                        <GridItem expanded={6}>
                            <Typography role='title'>
                                {labels.HubLabelUserPermissionCol2}
                            </Typography>
                            <Typography role='body'>
                                {labels.HubLabelUserPermissionDescriptionCol2}
                            </Typography>
                            {
                                !!this.state.clickedPermissionId && this.helper.isLeaf(this.state.clickedPermissionId) &&
                                <GridItem >
                                    <CheckboxTree
                                        icons={{
                                            check: <SerendieSymbol name='check-square' />,
                                            uncheck: <SerendieSymbol name='square' />,
                                            expandClose: <SerendieSymbol name='chevron-right' />,
                                            expandOpen: <SerendieSymbol name='chevron-down' />,
                                            halfCheck: <SerendieSymbol name='minus-square' />,
                                        }}
                                        nodes={this.nodeTree}
                                        checked={this.checkedNodeIds}
                                        expanded={this.props.expandedNodeIds}
                                        showNodeIcon={false}
                                        checkModel={'all'}
                                        onCheck={checkedNodeIds => this.handleNodeCheck(checkedNodeIds)}
                                        onExpand={expandedNodeIds => this.props.onNodeExpand(expandedNodeIds)}
                                    />
                                </GridItem>
                            }
                        </GridItem>
                        <GridItem>
                            <ButtonBar>
                                <RightButtons>
                                    <Button
                                        onClick={() => this.setState({ showDialog: true })}
                                    >
                                        {labels.HubLabelButtonJSONView}
                                    </Button>
                                </RightButtons>
                            </ButtonBar>
                        </GridItem>
                    </GridContainer>
                </Card>
                <AppPermissionsDialog
                    permissions={this.props.permissions}
                    show={this.state.showDialog}
                    onDialogClose={() => this.setState({ showDialog: false })}
                    onOk={permissions => this.props.onPermissionsChange(permissions)}
                />
            </>
        );
    }
}

export interface IState
{
    clickedPermissionId: string;
    showDialog: boolean;
}

export interface IProps
{
    permissions: IPermission[];
    onPermissionsChange: (permissions: IPermission[]) => void;
    expandedNodeIds: string[];
    onNodeExpand: (expandedNodeIds: string[]) => void;
}

export interface IPermission
{
    area: string;
    category: string;
    permission: string;
    isAvailable: boolean;
    isSelected: boolean;
    isExpanded: boolean;
    nodeIds: string[];
}
